import React, { useRef } from 'react';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { AddCashFundDialogLegacyLazy } from './AddCashFundDialogLegacy.lazy';
import { AddCashFundDialogV2Lazy } from './v2/AddCashFundDialogV2.lazy';
import type { AddCashFundDialogProps } from './types';

export const AddCashFundDialog: React.FC<AddCashFundDialogProps> = props => {
  const { isOpen } = props;
  const [isV2Enabled] = useIsFeatureEnabled('registryAddCashFundV2Enabled');
  const latchedIsV2Ref = useRef<boolean | null>(null);

  // Latched on open so a flag that resolves mid-flow can't swap the dialog out from under a host
  // part-way through creating a fund. Deliberately latches the still-loading value too: opening
  // during the flag fetch pins the legacy dialog for that session rather than swapping later.
  //
  // Never gate rendering on the flag's loading state — the remote-flag provider's `loading` can
  // stay true indefinitely when a dependency like Segment is blocked, which would leave the Add
  // Cash Fund button dead for every host. Falling through to legacy keeps today's behavior.
  if (!isOpen) {
    latchedIsV2Ref.current = null;
  } else if (latchedIsV2Ref.current === null) {
    latchedIsV2Ref.current = isV2Enabled;
  }

  if (latchedIsV2Ref.current ?? isV2Enabled) {
    return isOpen ? <AddCashFundDialogV2Lazy {...props} /> : null;
  }

  return <AddCashFundDialogLegacyLazy {...props} />;
};

AddCashFundDialog.displayName = 'AddCashFundDialog';
